import React from 'react'
import { Spacer, Text, View } from 'oio-react'
import pluralize from 'pluralize'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import LikeHeartIcon from 'assets/icons/likeHeart'
import MoreIcon from 'assets/icons/more'
import UserBadgeSash from 'src/apps/People/components/UserBadgeSash'
import RichText from 'src/components/utils/RichText'
import { useUpdateMessageReplyLiked } from 'src/graphql/hooks'
import MessagesContext from '~/components/Context'

// =======================================================
// Component
// =======================================================

const ReplyListItem = ({
   id,
   anon,
   authorAvatarUrl,
   authorBadges,
   authorId,
   authorName,
   body,
   borderStyle,
   currentUserCanModerate,
   dateAdded,
   edited,
   flaggedByMe,
   isLikedByMe,
   isMine,
   messageId,
   numLikes,
   textColor
}) => {
   const { updateMessageReplyLiked } = useUpdateMessageReplyLiked()
   const avatarUrl = authorAvatarUrl || '/assets/images/avatarPlaceholder.svg'

   const handleReplyLikeToggled = async (liked) => {
      await updateMessageReplyLiked({ replyId: id, messageId, liked }, {
         optimisticResponse: {
            updateMessageReplyLiked: {
               __typename: 'MessageReply',
               id,
               isLikedByMe: liked,
               numLikes: liked
                  ? numLikes + 1
                  : numLikes - 1
            }
         }
      })
   }

   return (
      <View
         float="left"
         width="100%"
         marginTop="15px"
         padding="15px 0px 0px 0px"
         borderTop={borderStyle}>
         <View display="flex">
            <View
               flex="0 0 auto"
               float="left"
               width="30px"
               height="30px"
               borderRadius="3px"
               backgroundColor="#ddd"
               backgroundImage={`url(${avatarUrl})`}
               backgroundSize={authorAvatarUrl ? 'cover' : '21px 21px'}
               backgroundPosition="bottom center"
               backgroundRepeat="no-repeat"
               marginRight="20px"
               marginTop="5px"
            />
            <View flex="1 1 auto" maxWidth="calc(100% - 50px)">
               <View display="flex" justifyContent="space-between">
                  <View
                     display="block[a-b] flex[c-f]"
                     alignItems="center">
                     <View display="inline-flex" alignItems="center" paddingRight="5px">
                        {!anon && (
                           <Link to={`/profile/${authorId}`}>
                              <Text size="2" color="#222" weight="medium">
                                 {anon ? 'Anonymous' : authorName}
                              </Text>
                           </Link>
                        )}
                        {anon && (
                           <Text size="2" color="#222" weight="medium">
                              Anonymous
                           </Text>
                        )}
                        {!anon && (
                           <UserBadgeSash badges={authorBadges} style={{ margin: '0 0 0 5px' }} />
                        )}
                     </View>
                     <View opacity="0.8">
                        <Text size="1.5" color="#222">
                           {dateAdded}
                           {edited && (
                              <View display="inline-block" marginLeft="6px" opacity="0.8">
                                 <Text size="1" color={textColor}>[edited]</Text>
                              </View>
                           )}
                        </Text>
                     </View>
                     {flaggedByMe && (
                        <View display="inline-block" marginLeft="5px">
                           <Text size="1" color="orange">[Reported]</Text>
                        </View>
                     )}
                  </View>
                  <MessagesContext.ThreadEntryOptionsButton
                     messageId={messageId}
                     replyId={id}
                     threadEntryBody={body}
                     threadEntryType="reply"
                     userCanEdit={isMine}
                     userCanRemove={isMine}
                     userCanReport={!isMine}>
                     <MoreIcon
                        width="24px"
                        height="24px"
                        color={textColor}
                     />
                  </MessagesContext.ThreadEntryOptionsButton>
               </View>
               <View float="left" width="100%">
                  <Text size="2" lineHeight="150%" color={textColor}>
                     <RichText html={body} />
                  </Text>
               </View>
               {numLikes > 0 && (
                  <View float="left" width="100%" marginTop="6px">
                     <Text
                        size="1"
                        lineHeight="150%"
                        color={textColor}>
                        {`${pluralize('people', numLikes, true)} like${numLikes === 1 ? 's' : ''} this`}
                     </Text>
                  </View>
               )}
               <View
                  float="left"
                  width="100%"
                  onClick={() => handleReplyLikeToggled(!isLikedByMe)}
                  style={{ cursor: 'pointer' }}>
                  <Spacer size="1" />
                  <View
                     display="inline-flex"
                     alignItems="center"
                     marginRight="18px"
                     opacity={isLikedByMe ? '1' : '0.5'}
                     style={{ cursor: 'pointer' }}>
                     {isLikedByMe && (
                        <View display="inline-block" paddingRight="6px">
                           <LikeHeartIcon width="12px" height="12px" color="#ff5b5b" />
                        </View>
                     )}
                     <Text
                        size="1"
                        weight="bold"
                        color={isLikedByMe ? '#ff5b5b' : textColor}>
                        {isLikedByMe ? 'Liked' : 'Like'}
                     </Text>
                  </View>
               </View>
            </View>
         </View>
      </View>
   )
}

ReplyListItem.propTypes = {
   id: PropTypes.string.isRequired,
   anon: PropTypes.bool.isRequired,
   authorAvatarUrl: PropTypes.string,
   authorBadges: PropTypes.array,
   authorId: PropTypes.string,
   authorName: PropTypes.string,
   body: PropTypes.string.isRequired,
   borderStyle: PropTypes.string.isRequired,
   currentUserCanModerate: PropTypes.bool.isRequired,
   dateAdded: PropTypes.string.isRequired,
   edited: PropTypes.bool.isRequired,
   flaggedByMe: PropTypes.bool,
   isLikedByMe: PropTypes.bool.isRequired,
   isMine: PropTypes.bool.isRequired,
   messageId: PropTypes.string.isRequired,
   numLikes: PropTypes.number.isRequired,
   textColor: PropTypes.string.isRequired
}

ReplyListItem.defaultProps = {
   authorAvatarUrl: undefined,
   authorBadges: [],
   authorId: undefined,
   authorName: undefined,
   flaggedByMe: false
}

export default ReplyListItem
